import { DownOutlined } from '@ant-design/icons';
import { Alert, Button, Dropdown, Space, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { CandidateCollection, JobPostingDetail } from '../../types';

interface ApplicantAnalysisBulkActionsProps {
  candidateIds: CandidateCollection['id'][];
  isHiredStage?: boolean;
  jobPostingStatus?: JobPostingDetail['status'];
  enableAlchemerIntegration?: boolean;
  questionsCount?: number;
  allCandidatesWhoAnsweredQuestions?: CandidateCollection['id'][];
  sendPracticalTechnicalCase?: boolean;
  onMoveStageClick?: () => void;
  onRejectClick?: () => void;
  onSendEmailClick?: () => void;
  onSendTestsClick?: () => void;
  onSendCustomTestsClick?: () => void;
  onSendAdditionalQuestionsClick?: () => void;
  onSendPracticalTechnicalCase?: () => void;
  onSelectClick?: () => void;
  onClear?: () => void;
}

const ApplicantAnalysisBulkActions = ({
  isHiredStage,
  candidateIds,
  jobPostingStatus,
  enableAlchemerIntegration,
  questionsCount,
  allCandidatesWhoAnsweredQuestions,
  sendPracticalTechnicalCase,
  onMoveStageClick,
  onRejectClick,
  onSendAdditionalQuestionsClick,
  onSendCustomTestsClick,
  onSendEmailClick,
  onSendTestsClick,
  onSendPracticalTechnicalCase,
  onSelectClick,
  onClear,
}: ApplicantAnalysisBulkActionsProps) => {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const candidatesWhoAnswered = allCandidatesWhoAnsweredQuestions?.filter((candidate) =>
    candidateIds.includes(candidate)
  );

  return (
    <Alert
      message={t('Você selecionou {{count}} candidato(s)', {
        count: candidateIds.length,
      })}
      action={
        <Space>
          {!isHiredStage && (
            <>
              {!['INACTIVE', 'GIVEN_UP'].includes(searchParams.get('status') || '') && (
                <>
                  {jobPostingStatus === 'IN_PROGRESS' && (
                    <Button onClick={onMoveStageClick}>
                      <Trans>Mover de etapa</Trans>
                    </Button>
                  )}
                  <Button onClick={onRejectClick}>
                    <Trans>Reprovar</Trans>
                  </Button>
                </>
              )}
            </>
          )}
          <Dropdown
            menu={{
              onClick: ({ key }) => {
                switch (key) {
                  case 'email':
                    onSendEmailClick?.();
                    break;
                  case 'tests':
                    onSendTestsClick?.();
                    break;
                  case 'custom-tests':
                    onSendCustomTestsClick?.();
                    break;
                  case 'additional_questions':
                    onSendAdditionalQuestionsClick?.();
                    break;
                  case 'practical-technical-case':
                    onSendPracticalTechnicalCase?.();
                    break;
                }
              },
              items: [
                { key: 'email', label: t('Enviar comunicação') },
                { key: 'tests', label: t('Enviar testes') },
                ...(enableAlchemerIntegration
                  ? [{ key: 'custom-tests', label: t('Enviar testes customizados') }]
                  : []),
                ...(questionsCount
                  ? [
                      {
                        key: 'additional_questions',
                        disabled:
                          candidatesWhoAnswered &&
                          candidatesWhoAnswered.length === candidateIds.length,
                        label: (
                          <Tooltip
                            title={
                              candidatesWhoAnswered &&
                              candidatesWhoAnswered.length === candidateIds.length
                                ? t('Os candidatos já responderam as perguntas de pré-requisito')
                                : ''
                            }
                          >
                            <Trans>Enviar perguntas de pré-requisito</Trans>
                          </Tooltip>
                        ),
                      },
                    ]
                  : []),
                ...(sendPracticalTechnicalCase
                  ? [
                      {
                        key: 'practical-technical-case',
                        label: t('Enviar case prático ou técnico'),
                      },
                    ]
                  : []),
              ],
            }}
          >
            <Button data-testid="bulk-actions-send-email">
              <Trans>Enviar e-mail</Trans> <DownOutlined />
            </Button>
          </Dropdown>
          <Button onClick={onSelectClick}>
            <Trans>Adicionar à vaga</Trans>
          </Button>
          <Button type="link" onClick={onClear}>
            <Trans>Limpar</Trans>
          </Button>
        </Space>
      }
    />
  );
};

export default ApplicantAnalysisBulkActions;
